import * as React from 'react';

import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';

import NavSingleItemDisplay from 'bundles/learner-progress/components/item/nav/private/NavSingleItemDisplay';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getReviewPartFromSplitPeer, getSubmitPartFromPeer } from 'bundles/learner-progress/utils/Item';
import { canShowDistinctAvailabilityReviewDates } from 'bundles/learner-progress/utils/canShowDistinctAvailabilityReviewDates';
import { useCourseAiGradingContext } from 'bundles/peer/components/ai-grading/CourseAiEligibilityProvider';
import waitForStores from 'bundles/phoenix/lib/waitForStores';

type PropsFromCaller = {
  computedItem: Item;
  highlighted?: boolean;
  pathname: string;
  trackingName?: string;
  openInNewWindow?: boolean;
  iconSize?: number;
};
export type Props = PropsFromCaller & {
  partnerIds: number[];
  isGuidedProject?: boolean;
};

export const NavPeerReviewItemDisplay = ({
  computedItem,
  highlighted,
  pathname,
  trackingName,
  openInNewWindow,
  iconSize,
  partnerIds,
  isGuidedProject,
}: Props) => {
  const showDistinctAvailabilityReviewDates = canShowDistinctAvailabilityReviewDates(partnerIds);
  const reviewItem = getReviewPartFromSplitPeer(computedItem, showDistinctAvailabilityReviewDates);
  const submitItem = getSubmitPartFromPeer(computedItem);
  /* eslint-disable no-warning-comments */
  // HACK: To handle cases where we want to change display
  // for assignment and review parts for peer review depending on which page is
  // being accessed, we directly check the current path name.
  // This is to highlight the review part in nav on the review part page.
  /* eslint-enable no-warning-comments */
  const shouldHighlightReviewItem = reviewItem && reviewItem.resourcePath === pathname;

  const { getItemFullyAiGradingEligibility, isLoading } = useCourseAiGradingContext();
  const isFullyAiGraded = getItemFullyAiGradingEligibility(computedItem.id);

  return (
    <span className="nostyle">
      <NavSingleItemDisplay
        computedItem={submitItem}
        highlighted={highlighted && !shouldHighlightReviewItem}
        trackingName={trackingName}
        openInNewWindow={openInNewWindow}
        iconSize={iconSize}
        isGuidedProject={isGuidedProject}
      />
      {!isLoading && !isFullyAiGraded && reviewItem && (
        <NavSingleItemDisplay
          computedItem={reviewItem}
          highlighted={highlighted && shouldHighlightReviewItem}
          trackingName={trackingName}
          openInNewWindow={openInNewWindow}
          iconSize={iconSize}
          showDistinctAvailabilityReviewDates={showDistinctAvailabilityReviewDates}
        />
      )}
    </span>
  );
};

export default compose<Props, Omit<Props, 'pathname'>>(
  connectToRouter<Props, Omit<Props, 'pathname'>>((router) => ({
    pathname: router.location.pathname,
  })),
  waitForStores(['CourseStore'], ({ CourseStore }, props) => {
    return {
      ...props,
      partnerIds: CourseStore.getMetadata()?.get('partnerIds'),
      isGuidedProject: CourseStore.isGuidedProject(),
    };
  })
)(NavPeerReviewItemDisplay);
